<template>
  <div class="media-contrast">
    <div class="contrast-header">
      <button class="btn-back" @click="$emit('close')">
        <i class="icon-back"></i>
      </button>
      <h3 class="title">分屏对比</h3>
      <div class="mode-switch">
        <button
          :class="['mode-btn', { 'active': mode === 'split' }]"
          @click="mode = 'split'"
        >
          分屏模式
        </button>
        <button
          :class="['mode-btn', { 'active': mode === 'slider' }]"
          @click="mode = 'slider'"
        >
          卷帘模式
        </button>
      </div>
      <button class="btn-reselect" @click="openMediaSelector">
        <i class="icon-refresh"></i>
        重新选择图片
      </button>
    </div>

    <div class="contrast-content">
      <!-- 分屏模式 -->
      <div v-if="mode === 'split'" class="split-mode">
        <div class="split-left">
          <div class="image-wrapper">
            <img :src="originImg" :alt="originImgName" class="contrast-image" />
            <div class="image-label">原图: {{ originImgName }}</div>
          </div>
        </div>
        <div class="split-right">
          <div class="image-wrapper">
            <img :src="selectedImg" :alt="selectedImgName" class="contrast-image" />
            <div class="image-label">对比图: {{ selectedImgName }}</div>
          </div>
        </div>
      </div>

      <!-- 卷帘模式 -->
      <div v-else class="slider-mode">
        <div class="slider-container">
          <div class="base-image">
            <img :src="originImg" :alt="originImgName" />
          </div>
          <div class="compare-image" :style="{ width: sliderPosition + '%' }">
            <img :src="selectedImg" :alt="selectedImgName" />
          </div>
          <div class="slider-handle" :style="{ left: sliderPosition + '%' }">
            <div class="slider-line"></div>
            <div class="slider-dot"></div>
          </div>
        </div>
        <div class="slider-controls">
          <span class="slider-label">原图</span>
          <input
            type="range"
            v-model="sliderPosition"
            min="0"
            max="100"
            class="slider-input"
          />
          <span class="slider-label">对比图</span>
        </div>
      </div>
    </div>

    <!-- 媒体选择器 -->
    <div v-if="showSelector" class="media-selector">
      <!-- 选择器内容 -->
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue'

const props = defineProps({
  originImg: String,
  originImgId: [String, Number],
  originImgName: String
})

const emit = defineEmits(['close'])

const mode = ref('split')
const selectedImg = ref('')
const selectedImgId = ref('')
const selectedImgName = ref('')
const showSelector = ref(false)
const sliderPosition = ref(50)

const openMediaSelector = () => {
  showSelector.value = true
}
</script>

<style lang="scss" scoped>
.media-contrast {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #0a0a0a;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;

  .contrast-header {
    display: flex;
    align-items: center;
    padding: 16px 24px;
    background: rgba(0, 0, 0, 0.8);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    gap: 24px;

    .btn-back {
      width: 36px;
      height: 36px;
      border-radius: 6px;
      border: 1px solid rgba(255, 255, 255, 0.15);
      background: rgba(255, 255, 255, 0.05);
      color: #fff;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.25);
      }

      i {
        font-size: 18px;
      }
    }

    .title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #fff;
    }

    .mode-switch {
      display: flex;
      gap: 8px;
      margin-left: auto;

      .mode-btn {
        padding: 8px 16px;
        border-radius: 6px;
        border: 1px solid rgba(255, 255, 255, 0.15);
        background: rgba(255, 255, 255, 0.05);
        color: rgba(255, 255, 255, 0.7);
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s ease;

        &.active {
          background: rgba(24, 144, 255, 0.2);
          border-color: rgba(24, 144, 255, 0.5);
          color: #1890ff;
        }

        &:hover:not(.active) {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.25);
        }
      }
    }

    .btn-reselect {
      padding: 8px 16px;
      border-radius: 6px;
      border: none;
      background: linear-gradient(135deg, #1890ff, #52c41a);
      color: #fff;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.2s ease;

      &:hover {
        background: linear-gradient(135deg, #40a9ff, #73d13d);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
      }
    }
  }

  .contrast-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .split-mode {
      display: flex;
      height: 100%;

      .split-left,
      .split-right {
        flex: 1;
        position: relative;
        overflow: hidden;

        .image-wrapper {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;

          .contrast-image {
            max-width: 100%;
            max-height: calc(100% - 40px);
            object-fit: contain;
            border-radius: 8px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
          }

          .image-label {
            margin-top: 12px;
            padding: 8px 16px;
            background: rgba(0, 0, 0, 0.7);
            border-radius: 4px;
            color: #fff;
            font-size: 14px;
            font-weight: 500;
          }
        }
      }

      .split-left {
        border-right: 1px solid rgba(255, 255, 255, 0.1);
      }
    }

    .slider-mode {
      flex: 1;
      display: flex;
      flex-direction: column;
      padding: 20px;

      .slider-container {
        flex: 1;
        position: relative;
        overflow: hidden;
        border-radius: 8px;

        .base-image,
        .compare-image {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;

          img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
        }

        .compare-image {
          border-right: 2px solid #1890ff;
        }

        .slider-handle {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 2px;
          transform: translateX(-1px);
          cursor: col-resize;
          z-index: 10;

          .slider-line {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            width: 2px;
            background: #1890ff;
          }

          .slider-dot {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: #1890ff;
            border: 3px solid #fff;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
          }
        }
      }

      .slider-controls {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-top: 20px;
        padding: 0 40px;

        .slider-label {
          color: #fff;
          font-size: 14px;
          font-weight: 500;
          min-width: 60px;
          text-align: center;
        }

        .slider-input {
          flex: 1;
          height: 6px;
          border-radius: 3px;
          background: rgba(255, 255, 255, 0.1);
          outline: none;
          appearance: none;
          cursor: pointer;

          &::-webkit-slider-thumb {
            appearance: none;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #1890ff;
            border: 3px solid #fff;
            cursor: grab;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
          }
        }
      }
    }
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 响应式设计
@media (max-width: 768px) {
  .media-contrast {
    .contrast-header {
      padding: 12px 16px;
      gap: 16px;
      flex-wrap: wrap;

      .mode-switch {
        order: 3;
        width: 100%;
        justify-content: center;
        margin-top: 12px;
      }

      .btn-reselect {
        margin-left: auto;
      }
    }

    .slider-mode {
      .slider-controls {
        padding: 0 20px;
      }
    }
  }
}
</style>
